import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, Clock, DollarSign, BookOpen, Search, Plus, X } from "lucide-react";
import { SiteShell } from "@/components/layout/SiteShell";
import { PlatformFeeNotice } from "@/components/marketplace/PlatformFeeNotice";
import EarlyAccessLimitedBanner from "@/components/EarlyAccessLimitedBanner";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { TID } from "@/constants/testIds";
import { toast } from "sonner";

export default function JobsList() {
  const { user } = useAuth();
  const isAuthor = user?.roles?.includes("author");
  const isAdmin = user?.roles?.includes("admin");
  const isPrivateTester = !!user?.is_private_test;
  const isRegularUser = !!user && !isAdmin && !isPrivateTester;
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ q: "", genre: "", max_budget: "", max_word_count: "", max_turnaround: "", max_spice: "" });
  const [mode, setMode] = useState("browse"); // browse | mine
  // When mode === "mine", flip between the author's active and archived
  // projects. Archived is hidden from everyone else — backend enforces
  // the owner/admin gate on `archived=true`.
  const [mineView, setMineView] = useState("active"); // active | archived

  const load = async () => {
    setLoading(true);
    try {
      const params = { ...filters };
      Object.keys(params).forEach((k) => (params[k] === "" || params[k] == null) && delete params[k]);
      if (mode === "mine") {
        params.mine = true;
        if (mineView === "archived") params.archived = true;
      }
      const { data } = await api.get("/jobs", { params });
      setJobs(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mode, mineView]);

  const empty = !loading && jobs.length === 0;

  return (
    <SiteShell>
      <section className="container-page py-10 md:py-14" data-testid={TID.jobs.listRoot}>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="chip"><Briefcase size={14} /> Marketplace</span>
            <h1 className="mt-3 font-serif text-4xl md:text-5xl tracking-tight">
              {mode === "mine"
                ? (mineView === "archived" ? "Archived projects" : "Your projects")
                : "Beta read opportunities"}
            </h1>
            <p className="mt-2 text-muted-foreground max-w-2xl">
              {mode === "mine"
                ? (mineView === "archived"
                    ? "Projects you've archived. Hidden from Discover and other users — all history, applications, and reviews are preserved. Open any to restore it."
                    : "Manage your posted projects, review applications, and track project status.")
                : "Thoughtful projects from authors who care about feedback. Filter to what fits you."}
            </p>
          </div>
          <div className="flex gap-2">
            {isAuthor && (
              <div className="inline-flex rounded-full border p-1 bg-card">
                <ModeChip active={mode === "browse"} onClick={() => setMode("browse")}>Browse</ModeChip>
                <ModeChip active={mode === "mine"} onClick={() => setMode("mine")}>My projects</ModeChip>
              </div>
            )}
            {isAuthor && mode === "mine" && (
              <div className="inline-flex rounded-full border p-1 bg-card" data-testid="my-projects-view-tabs">
                <ModeChip active={mineView === "active"} onClick={() => setMineView("active")} testid="my-projects-active-tab">Active</ModeChip>
                <ModeChip active={mineView === "archived"} onClick={() => setMineView("archived")} testid="my-projects-archived-tab">Archived</ModeChip>
              </div>
            )}
            {isAuthor && (
              <Link to="/jobs/new" data-testid={TID.jobs.newButton} className="btn-primary">
                <Plus size={16} /> Post a project
              </Link>
            )}
          </div>
        </div>

        {mode === "browse" && (
          <FilterBar filters={filters} setFilters={setFilters} onApply={load} />
        )}

        {mode === "browse" && isRegularUser && (
          <div className="mt-6 space-y-4" data-testid="jobs-list-demo-beta-notice">
            <EarlyAccessLimitedBanner context="jobs" />
            <PlatformFeeNotice variant="demo" />
          </div>
        )}

        <div className="mt-8">
          {loading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="card-soft h-52 animate-pulse" />)}
            </div>
          ) : empty ? (
            <EmptyState
              mode={mode}
              mineView={mineView}
              isAuthor={isAuthor}
              hasActiveFilters={Object.values(filters).some((v) => v !== "" && v != null)}
              onClearFilters={() => {
                setFilters({ q: "", genre: "", max_budget: "", max_word_count: "", max_turnaround: "", max_spice: "" });
                // Re-fetch with no filters immediately.
                setTimeout(load, 0);
              }}
            />
          ) : (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 stagger-fade">
              {jobs.map((j) => <JobCard key={j.id} job={j} />)}
            </div>
          )}
        </div>
      </section>
    </SiteShell>
  );
}

function ModeChip({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick}
      data-testid={testid}
      className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
        active ? "bg-[hsl(var(--sb-forest))] text-white" : "text-foreground/70 hover:bg-muted"
      }`}>{children}</button>
  );
}

function FilterBar({ filters, setFilters, onApply }) {
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clear = () => setFilters({ q: "", genre: "", max_budget: "", max_word_count: "", max_turnaround: "", max_spice: "" });
  return (
    <div className="mt-8 card-soft grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
      <div className="lg:col-span-2">
        <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Search</label>
        <div className="mt-1 relative">
          <Search size={16} className="absolute left-3 top-3.5 text-muted-foreground" />
          <input className="sb-input pl-9" placeholder="Title, tags, keywords" value={filters.q}
            onChange={(e) => set("q", e.target.value)} data-testid={TID.jobs.filterSearch} />
        </div>
      </div>
      <FilterField label="Genre" testid={TID.jobs.filterGenre}
        value={filters.genre} onChange={(v) => set("genre", v)} placeholder="e.g. Fantasy" />
      <FilterField label="Max words" testid={TID.jobs.filterWords} type="number"
        value={filters.max_word_count} onChange={(v) => set("max_word_count", v)} placeholder="e.g. 100000" />
      <FilterField label="Max budget" testid={TID.jobs.filterBudget} type="number"
        value={filters.max_budget} onChange={(v) => set("max_budget", v)} placeholder="USD" />
      <FilterField label="Max days" testid={TID.jobs.filterTurnaround} type="number"
        value={filters.max_turnaround} onChange={(v) => set("max_turnaround", v)} placeholder="e.g. 30" />
      <div>
        <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Max heat level</label>
        <select className="sb-input mt-1" value={filters.max_spice}
          onChange={(e) => set("max_spice", e.target.value)} data-testid={TID.jobs.filterSpice}>
          <option value="">Any</option>
          <option value="0">0 · Sweet</option>
          <option value="1">1 · Fade to Black</option>
          <option value="2">2 · Warm</option>
          <option value="3">3 · Open Door</option>
          <option value="4">4 · Steamy</option>
          <option value="5">5 · Erotica</option>
        </select>
      </div>
      <div className="lg:col-span-6 flex gap-2 justify-end">
        <button onClick={clear} data-testid={TID.jobs.filterClear} className="btn-ghost border">Clear</button>
        <button onClick={onApply} data-testid={TID.jobs.filterApply} className="btn-primary">Apply filters</button>
      </div>
    </div>
  );
}

function FilterField({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{label}</label>
      <input type={type} className="sb-input mt-1" placeholder={placeholder} value={value}
        onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}

export function JobCard({ job }) {
  const money = useMemo(() => {
    const c = job.currency || "USD";
    return `$${job.budget_min}–$${job.budget_max} ${c === "USD" ? "" : c}`;
  }, [job]);
  return (
    <Link to={`/jobs/${job.id}`} data-testid={TID.jobs.card} className="card-soft h-full block group">
      <div className="flex items-start gap-4">
        {job.cover_url && (
          <div
            className="shrink-0 w-14 rounded-md overflow-hidden border border-border/50 bg-muted"
            style={{
              aspectRatio: "2 / 3",
              backgroundImage: `url(${job.cover_url})`,
              backgroundSize: `${job.cover_position?.zoom ?? 100}% ${job.cover_position?.zoom ?? 100}%`,
              backgroundPosition: `${job.cover_position?.x ?? 50}% ${job.cover_position?.y ?? 50}%`,
              backgroundRepeat: "no-repeat",
            }}
            role="img"
            aria-label={`Cover: ${job.title || "project"}`}
            data-testid={`job-card-cover-${job.id}`}
          />
        )}
        <div className="min-w-0 flex-1 flex items-center justify-between gap-2 flex-wrap">
          <span className="chip">{job.genre || "Any genre"}</span>
          <StatusBadge status={job.status} />
          {job.listing_type === "demo" && (
            <span
              className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--sb-clay))]/50 bg-[hsl(var(--sb-clay))]/15 text-[hsl(var(--sb-clay))] text-[10px] font-bold uppercase tracking-[0.14em] px-2.5 py-1"
              data-testid={`job-card-demo-badge-${job.id}`}
              title="Demo/Test project — does not accept applications."
            >
              Demo / Test
            </span>
          )}
        </div>
      </div>
      <h3 className="mt-4 font-serif text-xl leading-snug line-clamp-2">{job.title}</h3>
      <p className="mt-2 text-sm text-muted-foreground line-clamp-3 leading-relaxed">{job.description}</p>
      <div className="mt-5 grid grid-cols-3 gap-2 text-xs text-muted-foreground">
        <Meta icon={BookOpen} label={`${(job.word_count || 0).toLocaleString()} words`} />
        <Meta icon={DollarSign} label={money} />
        <Meta icon={Clock} label={`${job.turnaround_days}d turnaround`} />
      </div>
      <div className="mt-4 pt-4 border-t flex items-center justify-between text-xs">
        <span className="text-muted-foreground">by {job.author_name || "Author"}</span>
        {job.listing_type === "demo" ? (
          <span
            className="font-bold uppercase tracking-[0.14em] text-[hsl(var(--sb-clay))]"
            data-testid={`job-card-demo-only-label-${job.id}`}
          >
            Demo only
          </span>
        ) : (
          <span className="text-muted-foreground inline-flex items-center gap-1.5 flex-wrap">
            {job.applications_closed ? (
              <span className="text-[hsl(var(--sb-clay))]" title="This project is not accepting new applications">
                Applications closed
              </span>
            ) : (
              <span>{job.applications_count || 0} applied</span>
            )}
            {(job.accepted_readers_count || 0) > 0 && (
              <span
                className="text-[hsl(var(--sb-forest-2))]"
                title="Beta readers the author has accepted"
                data-testid={`job-card-accepted-readers-${job.id}`}
              >
                · Accepted readers ({job.accepted_readers_count})
              </span>
            )}
          </span>
        )}
      </div>
    </Link>
  );
}

export function StatusBadge({ status }) {
  const map = {
    open:        { c: "bg-[hsl(var(--sb-moss))] text-[hsl(var(--sb-forest-2))]", label: "Open" },
    in_progress: { c: "!bg-[hsl(var(--sb-clay))] !text-[hsl(40_60%_96%)]", label: "In progress" },
    completed:   { c: "!bg-[hsl(var(--sb-forest))] !text-[hsl(40_60%_96%)]", label: "Completed" },
    cancelled:   { c: "!bg-red-600 !text-white", label: "Cancelled" },
  };
  const s = map[status] || map.open;
  return <span className={`text-[10px] uppercase tracking-[0.18em] rounded-full px-2.5 py-1 font-semibold ${s.c}`}>{s.label}</span>;
}

function Meta({ icon: Icon, label }) {
  return (
    <span className="inline-flex items-center gap-1.5"><Icon size={12} /> {label}</span>
  );
}

function EmptyState({ mode, mineView, isAuthor, hasActiveFilters, onClearFilters }) {
  const archived = mode === "mine" && mineView === "archived";
  const isBrowseWithFilters = mode === "browse" && hasActiveFilters;
  return (
    <div
      className="card-soft text-center py-16"
      data-testid={
        archived
          ? "my-projects-archived-empty"
          : isBrowseWithFilters
          ? "jobs-list-filtered-empty"
          : "jobs-list-empty"
      }
    >
      <Briefcase size={28} className="mx-auto text-muted-foreground" />
      <h3 className="mt-4 font-serif text-2xl">
        {archived
          ? "No archived projects"
          : mode === "mine"
          ? "No projects yet"
          : isBrowseWithFilters
          ? "No projects currently match these filters"
          : "No open projects right now"}
      </h3>
      <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
        {archived
          ? "Projects you archive will land here, hidden from Discover. You can restore them anytime."
          : mode === "mine"
          ? "Post your first project to invite beta readers."
          : isBrowseWithFilters
          ? "Try adjusting or clearing your filters, or check back as new projects are posted."
          : "The Early Access project pool is intentionally small while we onboard thoughtfully. Check back as new projects are posted."}
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-2">
        {isBrowseWithFilters && (
          <button
            type="button"
            onClick={onClearFilters}
            className="btn-primary inline-flex"
            data-testid="jobs-list-clear-filters-cta"
          >
            Show all projects
          </button>
        )}
        {isAuthor && mode === "mine" && (
          <Link to="/jobs/new" className="btn-primary inline-flex">Post your first project</Link>
        )}
      </div>
    </div>
  );
}
